import React, { useCallback, useEffect, useMemo, useState } from "react";
import ParticipantTable from "../../library/ParticipantTable/ParticipantTable";
import { ToastType } from "../../ToastManager/constants";
import { sendNotification } from "../../ToastManager/ToastManager";
import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import { connect } from "react-redux";
import {
  mapDispatchToProps,
  mapStateToProps,
  participantsReducer,
} from "./ParticipantTable.store";
import CustomModal from "../../ui-library/CustomModal/CustomModal";
import ConfirmRequest from "../../library/ConfirmRequest/ConfirmRequest";
import SmartChangeStudyStatus from "../ChangeStudyStatus/ChangeStudyStatus";
import SmartTemporaryPassword from "../TemporaryPassword/TemporaryPassword";
import SmartStudyStatusHistory from "../StudyStatusHistory/StudyStatusHistory";
import SmartAddParticipant from "../AddParticipant/AddParticipant";
import SmartEditParticipant from "../EditParticipant/EditParticipant";
import SmartPersonalData from "../PersonalData/PersonalData";
import ReportGeneratorOptions from "../../library/ReportGeneratorOptions/ReportGeneratorOptions";
import { smoothScrollToTop } from "../../../modules/utils";

export const SmartParticipantTable = (props) => {
  const {
    siteId,
    searchedParams,
    totalItems,
    participants,
    fetchParticipants,
    unlockUser,
    blockUnblockUser,
    canViewParticipantPage,
    canCreateParticipant,
    canEditParticipant,
    canViewParticipantTemporaryPassword,
    canUnLockParticipant,
    canChangeStudyStatus,
    canViewStudyStatus,
    studyId,
    canUnblockParticipant,
    canViewPersonalData,
    resetState,
    fetchEntriesData,
    canGenerateReport,
    loading,
  } = props;

  const [showChangeStudyStatusModal, setShowChangeStudyStatusModal] =
    useState(false);
  const [showTemporaryPasswordModal, setShowTemporaryPasswordModal] =
    useState(false);
  const [unlockingForTemporaryPassword, setUnlockingForTemporaryPassword] =
    useState(false);
  const [unblockingForTemporaryPassword, setUnblockingForTemporaryPassword] =
    useState(false);

  const [blockConfirmRequest, setBlockConfirmRequest] = useState(null);

  const [temporaryPasswordConfirmRequest, setTemporaryPasswordConfirmRequest] =
    useState(null);

  const [showStudyStatusHistoryModal, setShowStudyStatusHistoryModal] =
    useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showEditParticipantModal, setShowEditParticipantModal] =
    useState(false);

  const [showReportGeneratorModal, setShowReportGeneratorModal] =
    useState(false);

  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);

  const getData = useCallback(
    (params) => {
      fetchParticipants(siteId, params, studyId)
        .catch((error) => {
          sendNotification(error, ToastType.ERROR);
        })
        .finally(() => {
          if (params.limit !== searchedParams.limit) {
            smoothScrollToTop();
          }
        });
    },
    [siteId, studyId, fetchParticipants, searchedParams]
  );

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  const unlockAccount = (participant) => {
    unlockUser(participant.userName)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);
        if (canViewParticipantTemporaryPassword) {
          generateTemporaryPassword(participant, true, false);
        }

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const unblockAccount = (user) => {
    blockUnblockUser(user.userName, false)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);

        if (canViewParticipantTemporaryPassword) {
          generateTemporaryPassword(user, false, true);
        }

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const blockAccount = (user) => {
    blockUnblockUser(user.userName, true)
      .then((message) => {
        sendNotification(message, ToastType.SUCCESS);

        getData(searchedParams);
      })
      .catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
  };

  const closeChangeStudyStatusModal = (refreshData) => {
    setShowChangeStudyStatusModal(false);
    setSelectedParticipant(null);

    if (refreshData) {
      getData(searchedParams);
    }
  };

  const changeStudyStatus = (participant) => {
    setShowChangeStudyStatusModal(true);
    setSelectedParticipant(participant);
  };

  const closeTemporaryPasswordModal = () => {
    setShowTemporaryPasswordModal(false);
    setUnlockingForTemporaryPassword(false);
    setUnblockingForTemporaryPassword(false);
    setSelectedParticipant(null);
  };

  const generateTemporaryPassword = (
    participant,
    isUnlocking,
    isUnblocking
  ) => {
    setShowTemporaryPasswordModal(true);
    setUnlockingForTemporaryPassword(isUnlocking);
    setUnblockingForTemporaryPassword(isUnblocking);
    setSelectedParticipant(participant);
  };

  const closeStudyStatusHistoryModal = () => {
    setShowStudyStatusHistoryModal(false);
    setSelectedParticipant(null);
  };

  const openStudyStatusHistory = (participant) => {
    setShowStudyStatusHistoryModal(true);
    setSelectedParticipant(participant);
  };

  const closeAddParticipantModal = (participant) => {
    setShowAddParticipantModal(false);

    if (participant) {
      if (canViewParticipantTemporaryPassword) {
        generateTemporaryPassword(participant, false, false);
      }
    }

    getData(searchedParams);
  };

  const addParticipant = () => {
    setShowAddParticipantModal(true);
  };

  const openEditParticipantModal = (participant) => {
    setShowEditParticipantModal(true);
    setSelectedParticipant(participant);
  };

  const closeEditParticipantModal = () => {
    getData(searchedParams);
    setShowEditParticipantModal(false);
  };

  const closePersonalDataModal = () => {
    setShowPersonalDataModal(false);
    setSelectedParticipant(null);
  };

  const showPersonalData = (participant) => {
    setShowPersonalDataModal(true);
    setSelectedParticipant(participant);
  };

  const closeReportGeneratorModal = () => {
    setShowReportGeneratorModal(false);
    setSelectedParticipant(null);
  };

  const openGenerateReportOptions = (participant) => {
    setShowReportGeneratorModal(true);
    setSelectedParticipant(participant);
  };

  const closeConfirmRequestModal = () => {
    setTemporaryPasswordConfirmRequest(null);
    setBlockConfirmRequest(null);
  };

  const confirmRequestHandler = () => {
    if (temporaryPasswordConfirmRequest) {
      generateTemporaryPassword(temporaryPasswordConfirmRequest);
    } else if (blockConfirmRequest) {
      blockAccount(blockConfirmRequest);
    }

    closeConfirmRequestModal();
  };

  useEffect(() => {
    if (totalItems === null) {
      getData(searchedParams);
    }
  }, [getData, searchedParams, totalItems]);

  const confirmRequestMessage = useMemo(() => {
    if (temporaryPasswordConfirmRequest) {
      return "Clicking confirm will reset the user’s password. A new temporary password will be displayed on screen.";
    }

    if (blockConfirmRequest) {
      return "Clicking Confirm will block user from being able to login to Rarify. Unblocking a user will generate a new temporary password.";
    }

    return "";
  }, [temporaryPasswordConfirmRequest, blockConfirmRequest]);

  return (
    <>
      <CustomModal
        show={showAddParticipantModal}
        title="Add New Participant"
        onClose={() => setShowAddParticipantModal(false)}
      >
        <SmartAddParticipant
          onClose={closeAddParticipantModal}
          {...{ studyId }}
          {...{ siteId }}
        />
      </CustomModal>

      <CustomModal
        show={showChangeStudyStatusModal && !!selectedParticipant}
        title={`Changing ${selectedParticipant?.userName ?? ""} Study Status`}
        onClose={() => closeChangeStudyStatusModal(false)}
      >
        <SmartChangeStudyStatus
          onUpdate={() => closeChangeStudyStatusModal(true)}
          currentDailyTargets={selectedParticipant?.dailyTargets}
          studyEnrollmentID={selectedParticipant?.studyEnrollmentId}
          studyStatusID={selectedParticipant?.studyStatusId}
          {...{ studyId }}
        />
      </CustomModal>

      <CustomModal
        show={showTemporaryPasswordModal && !!selectedParticipant}
        title="Please use to login"
        onClose={closeTemporaryPasswordModal}
      >
        <SmartTemporaryPassword
          participantId={selectedParticipant?.participantId}
          ugnxId={selectedParticipant?.userName}
          onClose={closeTemporaryPasswordModal}
          unlocking={unlockingForTemporaryPassword}
          unblocking={unblockingForTemporaryPassword}
        />
      </CustomModal>

      <CustomModal
        show={showStudyStatusHistoryModal && !!selectedParticipant}
        title={`Study Status History for ${
          selectedParticipant?.userName ?? ""
        }`}
        onClose={closeStudyStatusHistoryModal}
      >
        <SmartStudyStatusHistory
          studyEnrollmentID={selectedParticipant?.studyEnrollmentId}
          onClose={closeStudyStatusHistoryModal}
          {...{ studyId }}
        />
      </CustomModal>

      <CustomModal
        show={showEditParticipantModal}
        title="Edit Participant"
        onClose={() => setShowEditParticipantModal(false)}
      >
        <SmartEditParticipant
          {...{ studyId }}
          participant={selectedParticipant}
          onClose={closeEditParticipantModal}
        />
      </CustomModal>

      <CustomModal
        show={showPersonalDataModal}
        title="Personal Data"
        onClose={closePersonalDataModal}
      >
        <SmartPersonalData
          onClose={closePersonalDataModal}
          participantId={selectedParticipant?.participantId}
        />
      </CustomModal>

      <CustomModal
        show={showReportGeneratorModal}
        title="Generate Report"
        onClose={closeReportGeneratorModal}
      >
        <ReportGeneratorOptions
          {...{ fetchEntriesData }}
          participant={selectedParticipant}
        />
      </CustomModal>

      <CustomModal
        title="Are you sure?"
        onClose={closeConfirmRequestModal}
        show={!!temporaryPasswordConfirmRequest || !!blockConfirmRequest}
      >
        <ConfirmRequest
          message={confirmRequestMessage}
          onConfirm={confirmRequestHandler}
          onClose={closeConfirmRequestModal}
        />
      </CustomModal>

      <ParticipantTable
        {...{ getData }}
        {...{ searchedParams }}
        totalitems={totalItems}
        loading={loading}
        {...{ participants }}
        {...{ unlockAccount }}
        {...{ changeStudyStatus }}
        generateTemporaryPassword={setTemporaryPasswordConfirmRequest}
        blockAccount={setBlockConfirmRequest}
        {...{ unblockAccount }}
        {...{ openStudyStatusHistory }}
        {...{ addParticipant }}
        {...{ canViewParticipantPage }}
        {...{ canCreateParticipant }}
        {...{ canEditParticipant }}
        {...{ canViewParticipantTemporaryPassword }}
        {...{ canUnLockParticipant }}
        {...{ canChangeStudyStatus }}
        {...{ canViewStudyStatus }}
        {...{ openEditParticipantModal }}
        {...{ canUnblockParticipant }}
        {...{ canViewPersonalData }}
        {...{ showPersonalData }}
        {...{ canGenerateReport }}
        {...{ openGenerateReportOptions }}
      />
    </>
  );
};

export default dynamic(
  "participantsReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(participantsReducer)
)(connect(mapStateToProps, mapDispatchToProps)(SmartParticipantTable));
